import { useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { apiName, apiBasePath } from 'assets/constants';
import { lowRelativeAbundanceLimit } from 'assets/constants';

interface SeedingTaxonomyResult {
  kit_id: string;
  taxonomy_category: string;
  taxonomy_name: string;
  classified_relative_abundance: number;
  category: 'Beneficial' | 'Unfriendly' | 'Variable' | null;
  // Use string literals matching potential DB values / enum values
  shared: 'both' | 'stool' | 'vaginal' | null;
  mom_stool_id: string | null;
  mom_vaginal_id: string | null;
}

// Define interface for the processed data pushed into arrays
export interface ProcessedBacteriaData extends SeedingTaxonomyResult {
  id: string; // Combined 'shared' + index
}

// Define interface for the final serialized result
export interface SerializedSeedingResult {
  kitId: string;
  sharedTaxaRelAbundance: number | null;
  sharedTaxa: {
    stool: ProcessedBacteriaData[];
    vaginal: ProcessedBacteriaData[];
    shared: ProcessedBacteriaData[];
  };
  environmentMicrobes: ProcessedBacteriaData[];
  environmentBreakdown: {
    beneficial: number;
    unfriendly: number;
    variable: number;
  };
  transferredMicrobes: ProcessedBacteriaData[];
  transferBreakdown: {
    beneficial: number;
    unfriendly: number;
    variable: number;
  };
}
async function getSeedingData(kitId: string) {
  const response = await API.get(
    apiName,
    `${apiBasePath}/reports/seeding/${kitId}?relativeAbundanceLimit=${lowRelativeAbundanceLimit}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.SEEDING,
        importance: IMPORTANCE.HIGH,
      },
      context: {
        kitId,
      },
    },
  );
  return response?.result;
}

const useSeeding = ({ kitId }: { kitId?: string | null }) => {
  const seedingQuery = useQuery<SerializedSeedingResult | null>({
    queryKey: [apiName, 'seeding', kitId],
    queryFn: () => (kitId ? getSeedingData(kitId) : null),
    enabled: !!kitId,
  });
  return seedingQuery;
};

export default useSeeding;
